import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Search, Image as ImageIcon, Loader2, Check } from "lucide-react";

/**
 * ImagePickerModal — three-tab modal to choose an image:
 *  1. Upload (file picker, auto-resize on backend, lands in WP Media)
 *  2. WP Media library (paginated grid + search)
 *  3. Original Travefy images for this itinerary
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onSelect: ({ url, alt? }) => void  ← parent decides what to do with the URL
 *   travefyImages?: string[]  ← list of Travefy CDN URLs (for tab 3)
 *   defaultAlt?: string       ← suggested alt for uploads (e.g. day title)
 */
export default function ImagePickerModal({
  open,
  onClose,
  onSelect,
  travefyImages = [],
  defaultAlt = "",
}) {
  const [tab, setTab] = useState("upload");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [media, setMedia] = useState({ items: [], total: 0, total_pages: 1 });
  const [mediaLoading, setMediaLoading] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [altText, setAltText] = useState(defaultAlt);

  useEffect(() => { setAltText(defaultAlt); }, [defaultAlt, open]);

  // Load WP Media when tab opens or search changes
  useEffect(() => {
    if (!open || tab !== "wp") return;
    let cancelled = false;
    (async () => {
      setMediaLoading(true);
      try {
        const r = await api.get("/itineraries/wp-media", { params: { search, page, per_page: 24 } });
        if (!cancelled) setMedia(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "No se pudo cargar WP Media");
      } finally {
        if (!cancelled) setMediaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, tab, search, page]);

  const onFileChosen = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Imagen demasiado grande (máx 10MB).");
      return;
    }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", altText || "");
      const { data } = await api.post("/itineraries/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Subida (${data.file_size_kb} KB · ${data.width}×${data.height})`);
      onSelect({ url: data.url, id: data.id, alt: altText });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploadBusy(false);
    }
  };

  const tabBtn = (id, label) => (
    <button
      type="button"
      onClick={() => { setTab(id); if (id !== "wp") setPage(1); }}
      className={`px-3 py-2 text-xs uppercase tracking-[0.18em] border-b-2 transition-colors ${
        tab === id
          ? "border-[#D95D39] text-[#0A1128] font-medium"
          : "border-transparent text-[#8A8F98] hover:text-[#0A1128]"
      }`}
      data-testid={`image-picker-tab-${id}`}
    >
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !uploadBusy && onClose()}>
      <DialogContent className="bg-white rounded-sm max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Elegir imagen</DialogTitle>
        </DialogHeader>

        <div className="border-b border-[#E2DCD0] flex gap-1 -mt-2">
          {tabBtn("upload", "Subir nueva")}
          {tabBtn("wp", `De WP Media (${media.total || "…"})`)}
          {tabBtn("travefy", `De Travefy (${travefyImages.length})`)}
        </div>

        {/* === Tab 1: Upload === */}
        {tab === "upload" && (
          <div className="py-6">
            <p className="text-xs text-[#8A8F98] mb-3">
              La imagen se optimiza y redimensiona a máx. 2000px de lado largo, y se sube a tu Media Library de WordPress.
            </p>
            <div className="mb-4">
              <label className="text-xs uppercase tracking-wider text-[#5C616A]">Texto alternativo (SEO)</label>
              <Input
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                placeholder="Ej: Sintra palaces and gardens at golden hour"
                className="mt-1 rounded-sm text-sm"
                data-testid="image-picker-alt-input"
              />
            </div>

            <label
              htmlFor="image-picker-file-input"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) onFileChosen(f);
              }}
              className={`border-2 border-dashed rounded-sm p-12 text-center cursor-pointer transition-colors block ${
                uploadBusy
                  ? "border-[#D95D39] bg-[#FAF7F2]"
                  : "border-[#E2DCD0] hover:border-[#D95D39] hover:bg-[#FAF7F2]"
              }`}
              data-testid="image-picker-drop-zone"
            >
              {uploadBusy ? (
                <>
                  <Loader2 className="w-8 h-8 mx-auto text-[#D95D39] animate-spin mb-3" />
                  <p className="font-medium text-[#0A1128]">Subiendo a WP Media…</p>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto text-[#8A8F98] mb-3" />
                  <p className="font-medium text-[#0A1128]">Haz click aquí o arrastra una imagen</p>
                  <p className="text-xs text-[#8A8F98] mt-1">JPG, PNG o WebP · máx 10MB</p>
                </>
              )}
              <input
                id="image-picker-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="sr-only"
                onChange={(e) => onFileChosen(e.target.files?.[0])}
                disabled={uploadBusy}
                data-testid="image-picker-file-input"
              />
            </label>
          </div>
        )}

        {/* === Tab 2: WP Media === */}
        {tab === "wp" && (
          <div className="flex flex-col flex-1 min-h-0 py-3">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-4 h-4 text-[#8A8F98]" />
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Buscar por nombre, título o alt..."
                className="rounded-sm text-sm"
                data-testid="image-picker-search-input"
              />
            </div>
            <div className="flex-1 overflow-y-auto pr-1">
              {mediaLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#D95D39]" /></div>
              ) : media.items.length === 0 ? (
                <div className="text-center text-[#8A8F98] py-8 text-sm">
                  <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  Ninguna imagen encontrada
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {media.items.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => { onSelect({ url: m.url, id: m.id, alt: m.alt }); onClose(); }}
                      className="group aspect-square overflow-hidden bg-[#F1ECE2] relative rounded-sm hover:ring-2 hover:ring-[#D95D39] transition-all"
                      title={m.title}
                      data-testid={`image-picker-wp-${m.id}`}
                    >
                      <img src={m.thumb} alt={m.alt} loading="lazy" className="w-full h-full object-cover" />
                      <span className="absolute inset-0 bg-[#0A1128]/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <Check className="w-6 h-6 text-white" />
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {media.total_pages > 1 && (
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#E2DCD0]">
                <Button
                  variant="outline" size="sm" disabled={page <= 1 || mediaLoading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-sm text-xs"
                >Anterior</Button>
                <span className="text-xs text-[#8A8F98]">Página {page} de {media.total_pages}</span>
                <Button
                  variant="outline" size="sm" disabled={page >= media.total_pages || mediaLoading}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-sm text-xs"
                >Siguiente</Button>
              </div>
            )}
          </div>
        )}

        {/* === Tab 3: Travefy === */}
        {tab === "travefy" && (
          <div className="py-3 overflow-y-auto pr-1">
            {travefyImages.length === 0 ? (
              <div className="text-center text-[#8A8F98] py-8 text-sm">Este itinerario no tiene imágenes originales de Travefy.</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {travefyImages.map((url, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { onSelect({ url, alt: altText }); onClose(); }}
                    className="group aspect-square overflow-hidden bg-[#F1ECE2] relative rounded-sm hover:ring-2 hover:ring-[#D95D39] transition-all"
                    data-testid={`image-picker-travefy-${i}`}
                  >
                    <img src={url} alt="" loading="lazy" className="w-full h-full object-cover" />
                    <span className="absolute inset-0 bg-[#0A1128]/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <Check className="w-6 h-6 text-white" />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
